import React from 'react';
import { EntityType } from './NetworkclubStep';
import { PartnerIcon, CommunityIcon, ThinkIcon } from '../../icons';

interface NetworkclubDetailsStepProps {
  data: {
    entityType: EntityType;
    networkclubName: string;
    networkclubKvk: string;
  };
  setData: (data: any) => void;
  invalidFields: Set<string>;
  setInvalidFields: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const NetworkclubDetailsStep: React.FC<NetworkclubDetailsStepProps> = ({
  data,
  setData,
  invalidFields,
  setInvalidFields
}) => {
  const updateField = (field: 'networkclubName' | 'networkclubKvk', value: string) => {
    setData({
      ...data,
      [field]: value
    });
    if (invalidFields.has(field)) {
      const next = new Set(invalidFields);
      next.delete(field);
      setInvalidFields(next);
    }
  };

  // Dynamic labels based on entity type
  const entityLabels = {
    networkclub: {
      title: 'Netwerkclub gegevens',
      description: 'Deze gegevens zijn verplicht voor netwerkclub-functionaliteit.',
      namePlaceholder: 'Naam van de netwerkclub',
      icon: <PartnerIcon size={20} weight="regular" className="text-gray-600" />
    },
    community: {
      title: 'Community gegevens',
      description: 'Deze gegevens zijn verplicht voor community-functionaliteit.',
      namePlaceholder: 'Naam van de community',
      icon: <CommunityIcon size={20} weight="regular" className="text-gray-600" />
    },
    mastermind: {
      title: 'Mastermind gegevens',
      description: 'Deze gegevens zijn verplicht voor mastermind-functionaliteit.',
      namePlaceholder: 'Naam van de mastermind groep',
      icon: <ThinkIcon size={20} weight="regular" className="text-gray-600" />
    }
  };

  const currentLabels = data.entityType && data.entityType !== null
    ? entityLabels[data.entityType]
    : entityLabels.networkclub;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">{currentLabels.icon} {currentLabels.title}</h2>
        <p className="text-gray-600">{currentLabels.description}</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Naam {data.entityType || 'organisatie'} *
        </label>
        <input
          type="text"
          value={data.networkclubName}
          onChange={(e) => updateField('networkclubName', e.target.value)}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
            invalidFields.has('networkclubName')
              ? 'border-[#B5724C] bg-amber-50 focus:ring-[#B5724C]'
              : 'border-gray-300 focus:ring-blue-500'
          }`}
          placeholder={currentLabels.namePlaceholder}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          KVK-nummer (8 cijfers) *
        </label>
        <input
          type="text"
          value={data.networkclubKvk}
          onChange={(e) => updateField('networkclubKvk', e.target.value)}
          className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:border-transparent ${
            invalidFields.has('networkclubKvk')
              ? 'border-[#B5724C] bg-amber-50 focus:ring-[#B5724C]'
              : 'border-gray-300 focus:ring-blue-500'
          }`}
          placeholder="12345678"
        />
        <p className="text-xs text-gray-500 mt-1">Alleen cijfers, exact 8 tekens.</p>
      </div>

      {/* Info message about subscription */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-900">
          <strong>ℹ️ Belangrijk:</strong> Om leden uit te nodigen en je {data.entityType || 'organisatie'} publiekelijk zichtbaar te maken,
          heb je een abonnement nodig. Zonder abonnement blijft de pagina alleen voor jou zichtbaar.
        </p>
      </div>
    </div>
  );
};

export default NetworkclubDetailsStep;
